import { useState, type FormEvent } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { getErrorMessage } from '../../utils/errors';
import { OAUTH_BASE_URL } from '../../config/constants';
import { Input } from '../../components/common/Input/Input';
import { Button } from '../../components/common/Button/Button';
import './Auth.css';


type RegisterField = 'name' | 'email' | 'password';
type RegisterErrors = Partial<Record<RegisterField, string>>;
type RegisterTouched = Partial<Record<RegisterField, boolean>>;

interface PasswordStrength {
  label: string;
  level: 1 | 2 | 3;
}

function Register() {
  const { t } = useTranslation();
  const { register } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState<RegisterErrors>({});
  const [serverError, setServerError] = useState('');
  const [loading, setLoading] = useState(false);
  const [touched, setTouched] = useState<RegisterTouched>({});

  const validate = (): RegisterErrors => {
    const errs: RegisterErrors = {};
    if (!name.trim()) {
      errs.name = t('auth.validation.nameRequired');
    } else if (name.trim().length < 2) {
      errs.name = t('auth.validation.nameMin');
    }
    if (!email.trim()) {
      errs.email = t('auth.validation.emailRequired');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      errs.email = t('auth.validation.emailInvalid');
    }
    if (!password) {
      errs.password = t('auth.validation.passwordRequired');
    } else if (password.length < 6) {
      errs.password = t('auth.validation.passwordMin');
    }
    return errs;
  };

  const handleBlur = (field: RegisterField) => {
    setTouched({ ...touched, [field]: true });
    const errs = validate();
    setErrors((prev) => ({ ...prev, [field]: errs[field] || '' }));
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setServerError('');
    const errs = validate();
    setErrors(errs);
    setTouched({ name: true, email: true, password: true });
    if (Object.keys(errs).length > 0) return;

    setLoading(true);
    try {
      await register(name, email, password);
      navigate('/');
    } catch (error) {
      setServerError(await getErrorMessage(error, t('auth.registerFailed')));
    } finally {
      setLoading(false);
    }
  };

  const isValid = (field: RegisterField): boolean => {
    if (!touched[field]) return false;
    if (errors[field]) return false;
    if (field === 'name') return name.trim().length >= 2;
    if (field === 'email') return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    if (field === 'password') return password.length >= 6;
    return false;
  };

  const passwordStrength = (): PasswordStrength | null => {
    if (password.length === 0) return null;
    let score = 0;
    if (password.length >= 6) score++;
    if (password.length >= 10) score++;
    if (/[A-Z]/.test(password)) score++;
    if (/[a-z]/.test(password)) score++;
    if (/[0-9]/.test(password)) score++;
    if (/[^A-Za-z0-9]/.test(password)) score++;

    if (score <= 2) return { label: t('auth.validation.weak'), level: 1 };
    if (score <= 4) return { label: t('auth.validation.medium'), level: 2 };
    return { label: t('auth.validation.strong'), level: 3 };
  };

  const strength = passwordStrength();

  return (
    <main className="auth-page">
      <div className="auth-card">
        <img src="/logo.png" alt="FCS" className="auth-logo" />
        <h1>{t('auth.register')}</h1>
        <p className="auth-subtitle">{t('auth.registerSubtitle')}</p>
        {serverError && <p className="auth-error">{serverError}</p>}

        <div className="auth-social">
          <a href={`${OAUTH_BASE_URL}/api/auth/oauth2/google`} className="auth-social-btn auth-google-btn">
            <svg viewBox="0 0 24 24" width="18" height="18">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
            </svg>
            {t('auth.continueGoogle')}
          </a>
        </div>

        <div className="auth-divider">
          <span>{t('auth.or')}</span>
        </div>

        <form onSubmit={handleSubmit} noValidate>
          <Input
            id="name"
            type="text"
            label={t('auth.name')}
            placeholder="John Doe"
            value={name}
            onChange={(e) => { setName(e.target.value); if (touched.name) { const errs = validate(); setErrors((prev) => ({ ...prev, name: errs.name || '' })); } }}
            onBlur={() => handleBlur('name')}
            error={errors.name}
            isTouched={touched.name}
            isValid={isValid('name')}
            floating={true}
            className="form-group"
          />

          <Input
            id="email"
            type="email"
            label={t('auth.email')}
            placeholder="example@email.com"
            value={email}
            onChange={(e) => { setEmail(e.target.value); if (touched.email) { const errs = validate(); setErrors((prev) => ({ ...prev, email: errs.email || '' })); } }}
            onBlur={() => handleBlur('email')}
            error={errors.email}
            isTouched={touched.email}
            isValid={isValid('email')}
            floating={true}
            className="form-group"
          />

          <Input
            id="password"
            type="password"
            label={t('auth.password')}
            placeholder="••••••••"
            value={password}
            onChange={(e) => { setPassword(e.target.value); if (touched.password) { const errs = validate(); setErrors((prev) => ({ ...prev, password: errs.password || '' })); } }}
            onBlur={() => handleBlur('password')}
            error={errors.password}
            isTouched={touched.password}
            isValid={isValid('password')}
            floating={true}
            className="form-group"
          />

          {strength && (
            <div className="password-strength">
              <div className="password-strength-bar">
                <div className={`password-strength-fill strength-${strength.level}`}></div>
              </div>
              <span className={`password-strength-label strength-${strength.level}`}>{strength.label}</span>
            </div>
          )}

          <Button
            type="submit"
            className="auth-submit-btn"
            loading={loading}
          >
            {t('auth.register')}
          </Button>
        </form>
        <p className="auth-footer">{t('auth.hasAccount')} <Link to="/login">{t('auth.login')}</Link></p>
      </div>
    </main>
  );
}

export default Register;
